import { Apartment } from '@/types';
import * as apartmentRepository from '@/repository/apartmentRepository';

/**
 * Generate a new apartment name by incrementing the number at the end
 * Examples: "Apartment 1" -> "Apartment 2", "Suite" -> "Suite 2"
 */
const generateDuplicateName = (originalName: string): string => {
  // Match a number at the end of the string (with optional spaces)
  const match = originalName.match(/^(.+?)(\s*)(\d+)$/);
  
  if (match) {
    // If name ends with a number, increment it
    const baseName = match[1];
    const spacing = match[2];
    const number = parseInt(match[3], 10);
    return `${baseName}${spacing}${number + 1}`;
  } else {
    // If no number at the end, append " 2"
    return `${originalName} 2`;
  }
};

export const apartmentService = {
  listenToApartments: (
    tripId: string, 
    callback: (apartments: Apartment[]) => void
  ): (() => void) => {
    return apartmentRepository.listenToApartments(tripId, callback);
  },

  addApartment: (
    tripId: string, 
    apartment: Omit<Apartment, 'id'>
  ): Promise<string> => {
    const newApartment = {
      ...apartment,
      currentOccupancy: 0,
      billStatus: 'not_sent' as const,
      isPaid: false,
    };
    return apartmentRepository.addApartment(tripId, newApartment);
  },

  updateApartment: (
    tripId: string, 
    apartmentId: string,
    data: Partial<Apartment>
  ): Promise<void> => {
    return apartmentRepository.updateApartment(tripId, apartmentId, data);
  },

  deleteApartment: (
    tripId: string, 
    apartmentId: string
  ): Promise<void> => {
    return apartmentRepository.deleteApartment(tripId, apartmentId);
  },

  duplicateApartment: async (
    tripId: string,
    apartment: Apartment
  ): Promise<string> => {
    // Create new apartment with duplicated data
    const duplicatedApartment: Omit<Apartment, 'id'> = {
      name: generateDuplicateName(apartment.name),
      class: apartment.class,
      maxOccupancy: apartment.maxOccupancy,
      currentOccupancy: 0, // Reset occupancy as per requirement
      apartmentTypeId: apartment.apartmentTypeId,
      amenities: [...apartment.amenities], // Copy amenities array
      billStatus: 'not_sent', // Reset billing status
      isPaid: false, // Reset payment status
    };

    return apartmentRepository.addApartment(tripId, duplicatedApartment);
  },
};
